import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  ControlLabel,
  EmptyState,
  Grid,
  MenuItem,
  SelectField,
  Stack,
} from '@all-manager/design-system-v2';
import { Line } from 'react-chartjs-2';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import CategoryTotalsTable from '../CategoryTotalsTable/CategoryTotalsTable';
import { useCategoryComparison } from './useCategoryComparison';

type Props = {
  categories: ExpenseCategoryDatabase[];
  expenses: ExpenseDatabase[];
};

export default function CategoryComparison(props: Props) {
  const hook = useCategoryComparison(props);

  if (!hook.hasEnoughCategories) {
    return (
      <Card>
        <EmptyState
          title="Une seule catégorie pour l'instant"
          subtitle="Créez une deuxième catégorie de dépenses pour comparer leurs montants mois par mois."
        />
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card>
          <CardHeader title="Catégories à comparer" subheader="Cochez au moins deux catégories." />
          <CardContent>
            <Stack>
              {props.categories.map((category) => (
                <ControlLabel
                  key={category.id}
                  label={category.name}
                  control={(
                    <Checkbox
                      checked={hook.selectedIds.includes(category.id)}
                      onChange={() => hook.handleToggleCategory(category.id)}
                    />
                  )}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        {hook.canCompare ? (
          <Stack spacing={2}>
            <div>
              <SelectField<number>
                label="Année"
                value={hook.selectedYear}
                onChange={(event) => hook.handleYearChange(Number(event.target.value))}
              >
                {hook.years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </SelectField>
            </div>

            <Card>
              <CardHeader title={`Dépenses mensuelles ${hook.selectedYear}`} />
              <CardContent>
                <Line
                  data={hook.chartData}
                  options={hook.chartOptions}
                  aria-label={`Comparaison mensuelle des catégories en ${hook.selectedYear}, totaux dans le tableau`}
                  role="img"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader title={`Totaux ${hook.selectedYear}`} />
              {hook.totals.length === 0 ? (
                <EmptyState
                  title={`Aucune dépense en ${hook.selectedYear}`}
                  subtitle="Les catégories cochées n'ont aucune dépense cette année. Choisissez une autre année."
                />
              ) : (
                <CategoryTotalsTable aria-label={`Totaux ${hook.selectedYear} par catégorie`} totals={hook.totals} />
              )}
            </Card>
          </Stack>
        ) : (
          <Card>
            <EmptyState
              title="Choisissez les catégories à comparer"
              subtitle={hook.missingCount === 1
                ? 'Cochez encore une catégorie pour afficher la comparaison.'
                : 'Cochez au moins deux catégories pour afficher la comparaison mois par mois.'}
            />
          </Card>
        )}
      </Grid>
    </Grid>
  );
}
